import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';
import { setCachedMessageTemplates } from '../../utils/messageTemplateCache';
import './MessagingTemplates.css';

type MessageTexts = {
  subject?: string;
  subjectEmpty?: string;
  subjectBooking?: string;
  subjectBookingEmpty?: string;
  body?: string;
  bodyBooking?: string;
  bodyThanks?: string;
  bodyFallback?: string;
  whatsapp?: string;
  whatsappBooking?: string;
};

type MetaRow = {
  id: string;
  group: 'shared' | 'client' | 'studio' | string;
  label: string;
  help?: string;
  tokens: string[];
  fields: string[];
};

type Preview = {
  subject?: string;
  text?: string;
  html?: string;
  whatsapp?: string;
};

const GROUP_LABELS: Record<string, string> = {
  client: 'To the client',
  studio: 'To Carmen',
  shared: 'Shared wording',
};

const FIELD_LABELS: Record<string, string> = {
  subject: 'Subject',
  subjectEmpty: 'Subject when the name or number is missing',
  subjectBooking: 'Subject (confirmed booking)',
  subjectBookingEmpty: 'Subject (booking, when the number is missing)',
  body: 'Body',
  bodyBooking: 'Body (confirmed booking)',
  bodyThanks: 'Thank-you line (automatic payment)',
  bodyFallback: 'Body when no amount is set',
  whatsapp: 'WhatsApp',
  whatsappBooking: 'WhatsApp (confirmed booking)',
};

const TOKEN_LABELS: Record<string, string> = {
  firstName: 'First name',
  clientName: 'Client name',
  quoteNumber: 'Quotation number',
  amount: 'Amount',
  remaining: 'Amount remaining',
  remainingSentence: 'Remaining sentence',
  dueDate: 'Due date',
  dueClause: 'Due clause',
  dueSentence: 'Due sentence',
  link: 'Page link',
  shootDate: 'Shoot date',
  startTime: 'Start time',
  location: 'Location',
  mapsUrl: 'Map link',
  changes: 'What changed',
  preferredWhen: 'Preferred date and time',
  place: 'Preferred place',
  placeClause: 'Place clause',
  note: 'Client note',
  noteSentence: 'Note sentence',
  replyText: 'Carmen\'s reply',
  whenPlace: 'When and where',
  map: 'Map block',
  page: 'Page block',
  opener: 'WhatsApp opener',
  openerNamed: 'WhatsApp opener with name',
};

const inertLinks = (html: string) => html.replace(
  '</body>',
  '<style>a{pointer-events:none;cursor:default}</style></body>'
);

const MessagingTemplates: React.FC = () => {
  const [meta, setMeta] = useState<MetaRow[]>([]);
  const [catalogue, setCatalogue] = useState<Record<string, MessageTexts>>({});
  const [draft, setDraft] = useState<Record<string, MessageTexts>>({});
  const [selected, setSelected] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const activeField = useRef<{ name: string; el: HTMLTextAreaElement | HTMLInputElement | null }>({ name: '', el: null });
  const navRef = useRef<HTMLElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLElement>(null);

  const applyPayload = useCallback((body: { catalogue?: { messages?: Record<string, MessageTexts> }; meta?: MetaRow[] }) => {
    const messages = (body.catalogue && body.catalogue.messages) || {};
    setCatalogue(messages);
    setDraft(messages);
    setCachedMessageTemplates({ version: 1, messages });
    if (Array.isArray(body.meta) && body.meta.length) {
      setMeta(body.meta);
      setSelected((current) => current || body.meta![0].id);
    }
    setDirty(false);
  }, []);

  const load = useCallback(async () => {
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/message-templates`, { credentials: 'include' });
      if (!response.ok) {
        setError('Messaging templates could not be loaded.');
        return;
      }
      const body = await response.json();
      applyPayload(body);
    } catch (err) {
      setError('Messaging templates could not be loaded.');
    }
  }, [applyPayload]);

  useEffect(() => {
    load();
  }, [load]);

  const currentMeta = meta.find((row) => row.id === selected) || meta[0];
  const currentId = currentMeta?.id || '';
  const currentDraft = draft[currentId] || {};

  const groups = useMemo(() => {
    const order = ['client', 'studio', 'shared'];
    return order.map((group) => ({
      id: group,
      label: GROUP_LABELS[group] || group,
      items: meta.filter((row) => row.group === group),
    })).filter((group) => group.items.length > 0);
  }, [meta]);

  const fieldsToShow = useMemo(() => {
    if (!currentMeta) return [] as string[];
    const fromMeta = currentMeta.fields.slice();
    const defaults = catalogue[currentId] || {};
    Object.keys(defaults).forEach((key) => {
      if (!fromMeta.includes(key)) fromMeta.push(key);
    });
    return fromMeta;
  }, [catalogue, currentId, currentMeta]);

  const updateField = (field: string, value: string) => {
    if (!currentId) return;
    setDraft((prev) => ({
      ...prev,
      [currentId]: { ...(prev[currentId] || {}), [field]: value },
    }));
    setDirty(true);
    setStatus('');
  };

  const insertToken = (token: string) => {
    const chip = `{${token}}`;
    const field = activeField.current.name || fieldsToShow[0] || 'body';
    const el = activeField.current.el;
    if (!currentId) return;
    const current = String((currentDraft as Record<string, string>)[field] || '');
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd || start;
      const next = `${current.slice(0, start)}${chip}${current.slice(end)}`;
      updateField(field, next);
      requestAnimationFrame(() => {
        if (activeField.current.el) {
          const pos = start + chip.length;
          activeField.current.el.focus();
          activeField.current.el.setSelectionRange(pos, pos);
        }
      });
      return;
    }
    updateField(field, `${current}${chip}`);
  };

  const runPreview = useCallback(async () => {
    if (!currentId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/studio/message-templates/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: currentId,
          catalogue: { messages: draft },
          booking: false,
        }),
      });
      if (!response.ok) {
        setPreview(null);
        return;
      }
      const body = await response.json();
      setPreview(body);
    } catch (err) {
      setPreview(null);
    }
  }, [currentId, draft]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      runPreview();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [runPreview]);

  useEffect(() => {
    const nav = navRef.current;
    const editor = editorRef.current;
    const preview = previewRef.current;
    if (editor) editor.scrollTop = 0;
    if (preview) preview.scrollTop = 0;
    if (!nav || !currentId) return;
    const active = nav.querySelector<HTMLButtonElement>('button.is-active');
    if (!active) return;
    const navRect = nav.getBoundingClientRect();
    const itemRect = active.getBoundingClientRect();
    if (itemRect.top < navRect.top) {
      nav.scrollTop -= navRect.top - itemRect.top;
    } else if (itemRect.bottom > navRect.bottom) {
      nav.scrollTop += itemRect.bottom - navRect.bottom;
    }
  }, [currentId]);

  const save = async () => {
    setSaving(true);
    setError('');
    setStatus('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/message-templates`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: 1, messages: draft }),
      });
      if (!response.ok) {
        setError('The messages could not be saved.');
        setSaving(false);
        return;
      }
      const body = await response.json();
      applyPayload(body);
      setStatus('Saved. New notices and alerts will use this wording.');
    } catch (err) {
      setError('The messages could not be saved.');
    }
    setSaving(false);
  };

  const resetCurrent = async () => {
    if (!currentId) return;
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/message-templates/reset`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: currentId }),
      });
      if (!response.ok) {
        setError('This message could not be reset.');
        setSaving(false);
        return;
      }
      const body = await response.json();
      applyPayload(body);
      setStatus('This message is back to the built-in wording.');
    } catch (err) {
      setError('This message could not be reset.');
    }
    setSaving(false);
  };

  return (
    <section className="messaging-templates" aria-label="Messaging">
      <header className="messaging-templates-header">
        <h2>Message templates</h2>
        <p>
          Edit the wording for studio emails, WhatsApp handoffs, and alerts to Carmen.
          Amounts, dates, and shoot details still come from the booking. Opening a preview does not send anything.
        </p>
      </header>

      <div className="messaging-templates-layout">
        <nav className="messaging-templates-nav" aria-label="Message list" ref={navRef}>
          {groups.map((group) => (
            <div key={group.id} className="messaging-templates-group">
              <h3>{group.label}</h3>
              <ul>
                {group.items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={item.id === currentId ? 'is-active' : ''}
                      onClick={() => setSelected(item.id)}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="messaging-templates-editor" ref={editorRef}>
          {currentMeta ? (
            <>
              <div className="messaging-templates-title-row">
                <h3>{currentMeta.label}</h3>
                <div className="messaging-templates-actions">
                  <button type="button" className="btn-secondary btn-sm" onClick={resetCurrent} disabled={saving}>
                    Reset this message
                  </button>
                  <button type="button" className="btn-primary btn-sm" onClick={save} disabled={saving || !dirty}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
              {currentMeta.help ? <p className="messaging-templates-help">{currentMeta.help}</p> : null}

              <div className="messaging-templates-tokens" aria-label="Insertable fields">
                <span>Fields</span>
                {currentMeta.tokens.map((token) => (
                  <button key={token} type="button" className="messaging-token" onClick={() => insertToken(token)}>
                    {TOKEN_LABELS[token] || token}
                  </button>
                ))}
                {['opener', 'page'].filter((token) => (currentDraft.whatsapp || '').includes(`{${token}}`) || fieldsToShow.includes('whatsapp')).map((token) => (
                  currentMeta.tokens.includes(token) ? null : (
                    <button key={token} type="button" className="messaging-token" onClick={() => insertToken(token)}>
                      {TOKEN_LABELS[token] || token}
                    </button>
                  )
                ))}
              </div>

              {fieldsToShow.map((field) => {
                const value = String((currentDraft as Record<string, string>)[field] ?? '');
                const multiline = !field.startsWith('subject');
                return (
                  <label key={field} className="messaging-field">
                    <span>{FIELD_LABELS[field] || field}</span>
                    {multiline ? (
                      <textarea
                        className="form-input"
                        rows={field.includes('whatsapp') || field.startsWith('body') ? 5 : 3}
                        value={value}
                        onFocus={(event) => {
                          activeField.current = { name: field, el: event.currentTarget };
                        }}
                        onChange={(event) => updateField(field, event.target.value)}
                      />
                    ) : (
                      <input
                        className="form-input"
                        type="text"
                        value={value}
                        onFocus={(event) => {
                          activeField.current = { name: field, el: event.currentTarget };
                        }}
                        onChange={(event) => updateField(field, event.target.value)}
                      />
                    )}
                  </label>
                );
              })}

              {status ? <p className="messaging-templates-status">{status}</p> : null}
              {error ? <p className="messaging-templates-error">{error}</p> : null}
            </>
          ) : (
            <p>Loading messages…</p>
          )}
        </div>

        <aside className="messaging-templates-preview" aria-label="Preview" ref={previewRef}>
          <h3>Preview</h3>
          <p className="messaging-templates-preview-note">Sample booking: Jordan Ellis · CLQ-2026-0142</p>
          {preview ? (
            <>
              {preview.subject ? <p className="messaging-preview-subject"><strong>{preview.subject}</strong></p> : null}
              {preview.html ? (
                <iframe title="Email preview" sandbox="" srcDoc={inertLinks(preview.html)} />
              ) : preview.text ? (
                <pre className="messaging-preview-text">{preview.text}</pre>
              ) : null}
              {preview.whatsapp ? (
                <div className="messaging-preview-whatsapp">
                  <h4>WhatsApp</h4>
                  <pre>{preview.whatsapp}</pre>
                </div>
              ) : null}
            </>
          ) : (
            <p>Choose a message to preview.</p>
          )}
        </aside>
      </div>
    </section>
  );
};

export default MessagingTemplates;
